import { WithEnterpriseId, WithServiceId } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { RouteName } from '@mews-commander/enums';
import { LocalizedProperty, lt } from '@mews/framework';
import { Stack, Container } from '@optimus-web/core';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getServiceNames } from '@mews-commander/legacy-entities/src/ducks/services';
import { useCommanderSelector } from '@mews-commander/utils';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { FilterSidebarLayoutBase, FilterSidebarLayoutContent } from '../../../components/layout';
import { filterSchema, queryParamsSchema } from '../schemas';
import { useDefaultFilterValues } from '../queries';
import { FilterSidebarFields } from './filterFields';
import { SellLimitActions } from './SellLimitActions';
import { SellLimitsMatrix } from './SellLimitsMatrix';
import { SellLimitsTitle } from './SellLimitsTitle';

export const SellLimits = ({ enterpriseId, serviceId }: WithEnterpriseId & WithServiceId) => {
    const resolveRoute = useResolveRoute();

    const enterpriseUrl = resolveRoute(RouteName.EnterpriseDetail, { enterpriseId });
    const enterpriseName = useCommanderSelector((state) => getEnterpriseName(state, { enterpriseId }));

    const serviceListUrl = resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: enterpriseId } });

    const serviceNames = useCommanderSelector((state) => getServiceNames(state, { id: serviceId }));
    const serviceUrl = resolveRoute(RouteName.VisitDetail, { serviceId });

    const breadcrumbs: Breadcrumbs = [
        {
            text: enterpriseName,
            url: enterpriseUrl,
        },
        {
            text: lt('ServicePlural'),
            url: serviceListUrl,
        },
        {
            text: serviceNames ? <LocalizedProperty property={serviceNames} /> : lt('Service'),
            url: serviceUrl,
        },
        {
            text: lt('SellLimits'),
            active: true,
        },
    ];

    const ownerProps = { enterpriseId, serviceId };
    const defaultFilterValues = useDefaultFilterValues(ownerProps);

    return (
        <FilterSidebarLayoutBase
            title={lt('SellLimits')}
            breadcrumbs={breadcrumbs}
            loading={defaultFilterValues.isFetching}
        >
            {(sidebarToggleProps) =>
                defaultFilterValues.isSuccess ? (
                    <FilterSidebarLayoutContent
                        {...sidebarToggleProps}
                        filterSchema={filterSchema}
                        defaultFilterValues={defaultFilterValues.data}
                        queryParamsSchema={queryParamsSchema.merge(filterSchema).partial()}
                        defaultQueryParams={{
                            OwnerId: serviceId,
                        }}
                        filterSidebar={{
                            title: lt('SellLimits'),
                            fields: <FilterSidebarFields {...ownerProps} />,
                        }}
                    >
                        {({ filterValues }) => (
                            <>
                                <Container padding={['200', '300']}>
                                    <Stack distribution="space-between">
                                        <SellLimitsTitle
                                            enterpriseId={enterpriseId}
                                            serviceId={serviceId}
                                            view={filterValues.view}
                                        />
                                        <SellLimitActions {...ownerProps} />
                                    </Stack>
                                </Container>
                                <SellLimitsMatrix
                                    serviceId={serviceId}
                                    filterValues={filterValues}
                                    enterpriseId={enterpriseId}
                                />
                            </>
                        )}
                    </FilterSidebarLayoutContent>
                ) : null
            }
        </FilterSidebarLayoutBase>
    );
};
